import { useEffect, useRef, useState } from "react";
import { GamgeeChatMessageRead, RichMessage } from "~/types/message";
import MarkdownContainer from "~/components/markdown-container/markdown-container";
import useResizeObserver from "@react-hook/resize-observer";
import { getEventStream } from "~/lib/api";
import { useGlobalStore } from "~/stores/store-provider";
import styles from "./rich-gamgee-message.module.css";
import { parseEventMessage, getMessagesToRender } from "./utils";
import ToolSection from "./tool-section";

// Define the loader component
const SmallRingLoader = () => (
  <div className={styles.smallRingLoader}>
    <div className={styles.smallRing}></div>
  </div>
);

export default function RichGamgeeMessage(
  { message, scrollToBottom }: { message: GamgeeChatMessageRead, scrollToBottom: () => void }
) {
  const { content, streaming } = message.message as RichMessage;
  const [messageChunks, setMessageChunks] = useState<any[]>([]);
  const [messagesToRender, setMessagesToRender] = useState<any[]>([]);
  const toolMapping = useRef<any>({});
  const containerRef = useRef<HTMLDivElement>(null);

  const { markRichStreamingFinished } = useGlobalStore((state: any) => state);

  useResizeObserver(containerRef, (entry) => {
    scrollToBottom();
  });

  useEffect(() => {
    if (streaming) {
      const eventSource = getEventStream(
        `/api/v1/chat/connectToStream?session_id=${message.session_id}&message_id=${message._id}`
      );
      eventSource.addEventListener("message", (event) => {
        const { channelName, messages } = parseEventMessage(event.data);
        setMessageChunks((prev) => [...prev, ...messages]);
        setMessagesToRender((prev) => getMessagesToRender(toolMapping.current, prev, messages));
        scrollToBottom();
      });
      eventSource.addEventListener("error", (event) => {
        console.log("Event source error:", event);
        markRichStreamingFinished(message._id);
        eventSource.close();
      });
    } else {
      const buildResponse = getMessagesToRender(toolMapping.current, [], content);
      setMessagesToRender(buildResponse);
    }
  }, []);

  const getElementFromMessage = (messageItem: any) => {
    if (typeof messageItem === "string") {
      return (
        <MarkdownContainer message={message}>
          {messageItem}
        </MarkdownContainer>
      );
    } else if (messageItem.type === "tool") {
      return (
        <ToolSection
          toolTitle={messageItem.tool_title}
          content={messageItem.content}
          loading={messageItem.loading}
          message={message}
        />
      );
    } else {
      return null;
    }
  }

  return (
    <div className={"flex flex-col gap-3"} ref={containerRef}>
      {messagesToRender.map((messageItem, index) => (
        <div key={index}>
          {getElementFromMessage(messageItem)}
        </div>
      ))}
      {streaming && <SmallRingLoader />}
    </div>
  );
}
